<script>
    import {abortableGet, buildElementIdFromPageNumber, buildElementIdFromRowId, updateUrlParam} from '../../utils.js';
    import SortableTable from '../DataGrid/table/SortableTable.svelte';
    import TableRow from '../DataGrid/table/TableRow.svelte';
    import TableColumn from '../DataGrid/table/TableColumn.svelte';
    import TypeRenderer from './columnRenderer/TypeRenderer.svelte';
    import ReferenceRenderer from './columnRenderer/ReferenceRenderer.svelte';
    import AgeRenderer from './columnRenderer/AgeRenderer.svelte';
    import DescriptionRenderer from './columnRenderer/DescriptionRenderer.svelte';
    import SupervisorInputRenderer from './columnRenderer/SupervisorInputRenderer.svelte';
    import PriorityRenderer from './columnRenderer/PriorityRenderer.svelte';
    import ClassificationRenderer from './columnRenderer/ClassificationRenderer.svelte';
    import DelaySizeRenderer from './columnRenderer/DelaySizeRenderer.svelte';
    import KeywordSearch from '../DataGrid/table/KeywordSearch.svelte';
    import {ISSUE_LIST_API} from '../../apiUrls.js';
    import {flagTypes, flagStatus, ISSUE_PAGE_SAVED_PAGE_NUMBER, ISSUE_PAGE_SAVED_ROW_ID} from '../../constants.js';
    import {sgettext} from '../../i18n.js';
    import { afterUpdate } from 'svelte';
    import { get } from "../../utils.js";

    export let type = flagTypes.improvement;
    export let status = 'OPEN';
    export let activityInstanceStatus = '';
    export let branchId = 270;

    let totalItems = 0;
    let pageSize = 100;
    let nextUrl = '';
    let previousUrl = '';
    let data = [];
    let keyword = '';
    let loading = false;
    let request = abortableGet();
    let reloadAfterAbort = true;
    let params = '';
    let searching = false;
    let clearing = false;
    let childTable = null;

    let showSubBranchesContent = false;

    let columns = [
        {
            key: 'type',
            label: sgettext('$$TYPE$$'),
            renderer: TypeRenderer,
            order: 0,
            hidden: type === flagTypes.help
        },
        {
            key: 'value',
            label: sgettext('$$DESCRIPTION$$'),
            renderer: DescriptionRenderer,
            order: 1,
            width: '400px'
        },
        {
            key: 'user',
            label: sgettext('$$REPORTER$$'),
            sortable: true,
            order: 2
        },
        {
            key: 'work_order',
            label: sgettext('$$WORK_ORDER$$'),
            order: 3
        },
        {
            key: 'activity_name',
            label: sgettext('$$ACTIVITY_1$$'),
            order: 4,
            width: '300px'
        },
        {
            key: 'classification',
            label: sgettext('$$CLASSIFICATION$$'),
            order: 5,
            width: '100px'
        },
        {
            key: 'created_date',
            label: sgettext('$$AGE$$'),
            renderer: AgeRenderer,
            order: 6,
            sortable: true,
            reverse: true
        },
        {
            key: 'delay_size',
            label: sgettext('$$DELAY_SIZE$$'),
            headers: 'supervisor',
            renderer: DelaySizeRenderer,
            order: 7
        },
        {
            key: 'priority',
            label: sgettext('$$PRIORITY$$'),
            renderer: PriorityRenderer,
            headers: 'supervisor',
            sortable: true,
            order: 8
        },
        {
            key: 'supervisor_input',
            label: sgettext('$$SUPERVISOR_INPUT$$'),
            headers: 'supervisor',
            renderer: SupervisorInputRenderer,
            order: 9
        }];

    $: toggleTypeColumn(type);

    $: getIssuesParams(branchId, type, status, keyword, showSubBranchesContent);

    function toggleTypeColumn(type) {
        const typeCol = columns.find(c => c.key === 'type');
        if (type === flagTypes.help) {
            typeCol.hidden = true;
        } else {
            typeCol.hidden = false;
        }
        columns = [...columns];
    }

    function getIssuesParams(branchId, type, status, keyword, showSubBranchesContent) {
        const activityInstanceStatusFilter = activityInstanceStatus ? `&activity_instance_status=${activityInstanceStatus}` : '';

        params = `branch_id=${branchId}&type=${type}&status=${status}${activityInstanceStatusFilter}&show_subBranches_content=${showSubBranchesContent}`;

        if (keyword) {
            params += `&value=${keyword}`;
        }
    }

    function setStatus(newStatus) {
      status = newStatus
      updateUrlParam('status', newStatus);
    }

    function onDataSuccess(result) {
        searching = false;
        clearing = false;
        data = result;
    }

    function onDataFail(error){
        searching = false;
        clearing = false;
    }

    function onSearch(value) {
        if (value) {
            keyword = value;
            searching = true;
        }
    }

    function onClearSearch() {
        if (keyword) {
            clearing = true;
            keyword = '';
        }
    }

    afterUpdate(async () => {
        const savedPageNumber = sessionStorage.getItem(ISSUE_PAGE_SAVED_PAGE_NUMBER);
        if (savedPageNumber != null)
        {
            if (childTable.getCurrentPage() != parseInt(savedPageNumber))
            {
                //this will start reloading the required page content
                document.getElementById(buildElementIdFromPageNumber(savedPageNumber)).click();
            }
            else
            {
                // when the required page is loaded, then scroll to the required row
                const savedRowId = sessionStorage.getItem(ISSUE_PAGE_SAVED_ROW_ID);
                if (savedRowId != null)
                {  
                    document.getElementById(buildElementIdFromRowId(savedRowId)).scrollIntoView();
                    //reset the page link and row id storage after this
                    sessionStorage.removeItem(ISSUE_PAGE_SAVED_PAGE_NUMBER);
                    sessionStorage.removeItem(ISSUE_PAGE_SAVED_ROW_ID);
                }
            }
        }
    });

    async function changeCheckboxValue(event) {
        showSubBranchesContent = event.currentTarget.checked;
    }

</script>

<style>
    .issue-table :global(.group-header) {
        color: white;
        text-align: center;
    }

    .issue-table :global(#supervisor) {
        border-left: 1px solid #dddddd;
        border-right: 1px solid #dddddd;
    }

    .issue-table :global(.column-header),
    .issue-table :global(.footer-row) {
        background-color: #dee2e6;
    }

    .issue-table :global(td[headers="supervisor"]) {
        background-color: rgba(150, 150, 150, 0.1);
    }

    .issue-table :global(.table-striped-even tbody tr:nth-child(even)) {
        background-color: rgba(33, 37, 41, 0.03);
    }

    .top-bar {
        display: flex;
        padding: 10px 0;
        justify-content: space-between;
        align-items: center;
    }

    .action-bar {
        display: flex;
    }

    .nav-tabs {
      border-bottom: 0;
    }

    .nav-link {
      outline: none;
      background-color: transparent;
    }

    .show-subbranch-content {
        margin-left:40%; 
        margin-top:7px;
    }
</style>

<div class="top-bar">
    <div>
      <ul class="nav nav-tabs mr-auto">
        <li class="nav-item">
          <button
            on:click={() => setStatus(flagStatus.open)}
            class="nav-link font-weight-bold"
            class:active={status.toLocaleLowerCase() === "open"}
          >
            {sgettext('$$OPEN$$')}
          </button>
        </li>
        <li class="nav-item">
          <button
            on:click={() => setStatus(flagStatus.closed)}
            class="nav-link font-weight-bold"
            class:active={status.toLocaleLowerCase() === "closed"}
          >
            {sgettext('$$RESOLVED$$')}
          </button>
        </li>
      </ul>
    </div>
    <div class="show-subbranch-content">
        <input
            type="checkbox"
            on:change={event => changeCheckboxValue(event)}
        />
        {sgettext('$$SHOW_CONTENT_SUBBRANCHES$$')}
    </div>
    <div class="action-bar">
        <KeywordSearch
          {searching}
          {clearing}
          {onSearch}
          {onClearSearch}
        />
    </div>
</div>
<div class="issue-table">
    <SortableTable class="table-striped border" bind:this={childTable} {columns} {pageSize} {params} dataUrl="{ISSUE_LIST_API}" {onDataSuccess} {onDataFail}>
        <thead slot="header">
            <TableRow class="group-header bg-gray-600">
                <TableColumn id="issueDetails" colspan={type === flagTypes.improvement ? 6 : 5}>{sgettext('$$ISSUE_DETAILS$$')}
                </TableColumn>
                <TableColumn id="supervisor" colspan={4}>{sgettext('$$SUPERVISOR$$')}</TableColumn>
                <TableColumn id="action">{sgettext('$$INVESTIGATEACTION$$')}</TableColumn>
            </TableRow>
            <TableRow class="column-header">
                {#each columns.filter(c => !c.hidden) as col}
                    <TableColumn
                            key="{col.key}"
                            sortable="{col.sortable}"
                            reverse="{col.reverse}"
                            isHeader="{true}"
                            width="{col.width}"
                            headers="{col.headers}">{col.label}</TableColumn>
                {/each}
                <TableColumn isHeader="{true}">{sgettext('$$REFERENCE$$')}</TableColumn>
            </TableRow>
        </thead>
        {#each data as row}
            <TableRow rowId={buildElementIdFromRowId(row.id)}>
                {#each columns.filter(c => !c.hidden) as k}
                    <TableColumn headers={k.headers ? k.headers : false}>
                        {#if k.renderer}
                            <svelte:component this={k.renderer} {row}/>
                        {:else}
                            {row[k.key] ? row[k.key] : ''}
                        {/if}
                    </TableColumn>
                {/each}
                <TableColumn>
                    <ReferenceRenderer {row}/>
                </TableColumn>
            </TableRow>
        {/each}
    </SortableTable>
</div>
